import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../util/Toast';
import {
  Search,
  Users,
  UserPlus,
  ShieldAlert,
  Trash2,
  CheckCircle2,
  XCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  AlertTriangle,
  X,
} from 'lucide-react';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const ROWS_PER_PAGE = 10;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all | active | pending | banned
  const [currentPage, setCurrentPage] = useState(1);

  const [actionLoading, setActionLoading] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { type, user }

  // Pending modal
  const [isPendingModalOpen, setIsPendingModalOpen] = useState(false);
  const [modalAnimating, setModalAnimating] = useState(false);
  const [pendingSearch, setPendingSearch] = useState('');

  // ==================== FETCH ====================
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.get(`${baseUrl}/api/admin/users`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (response.data?.status) {
        setUsers(response.data.users || []);
        setPending(response.data.pending || []);
      } else {
        toast.error(response.data?.msg || 'Failed to load users');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error(error.response?.data?.msg || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ==================== FILTER ====================
  const filteredUsers = useMemo(() => {
    let list = users || [];

    if (statusFilter !== 'all') {
      list = list.filter((u) => u.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (u) =>
          (u.name || '').toLowerCase().includes(q) ||
          (u.email || '').toLowerCase().includes(q) ||
          (u.number || '').toLowerCase().includes(q) ||
          (u.id || '').toLowerCase().includes(q)
      );
    }

    return list;
  }, [users, statusFilter, searchQuery]);

  // Reset page on filter/search change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  // ==================== PAGINATION ====================
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / ROWS_PER_PAGE));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * ROWS_PER_PAGE;
    return filteredUsers.slice(start, start + ROWS_PER_PAGE);
  }, [filteredUsers, currentPage]);

  // ==================== ACTIONS ====================
  const performAction = async (userId, action) => {
    setActionLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_URL || '';
      const token = getCookie('adminToken') || getCookie('authToken');

      const response = await axios.post(
        `${baseUrl}/api/admin/users/action`,
        { ids: [userId], action },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );

      if (response.data?.status) {
        toast.success(response.data.msg || `User ${action} successful`);
        setConfirmAction(null);
        fetchUsers();
      } else {
        toast.error(response.data?.msg || `Failed to ${action}`);
      }
    } catch (error) {
      console.error(`Error on ${action}:`, error);
      toast.error(error.response?.data?.msg || `Failed to ${action}`);
    } finally {
      setActionLoading(false);
    }
  };

  const openConfirm = (type, user) => {
    setConfirmAction({ type, user });
  };

  const closeConfirm = () => {
    setConfirmAction(null);
  };

  // ==================== PENDING MODAL ====================
  const openPendingModal = () => {
    setIsPendingModalOpen(true);
    requestAnimationFrame(() => setModalAnimating(true));
  };

  const closePendingModal = () => {
    setModalAnimating(false);
    setTimeout(() => {
      setIsPendingModalOpen(false);
      setPendingSearch('');
    }, 300);
  };

  const filteredPending = useMemo(() => {
    if (!pendingSearch.trim()) return pending;
    const q = pendingSearch.toLowerCase();
    return pending.filter(
      (p) =>
        (p.name || '').toLowerCase().includes(q) ||
        (p.email || '').toLowerCase().includes(q) ||
        (p.number || '').toLowerCase().includes(q)
    );
  }, [pending, pendingSearch]);

  // ==================== STATUS HELPERS ====================
  const statusBadge = (status) => {
    const s = String(status || '').toLowerCase();
    if (s === 'active') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-lg text-[10px] font-black uppercase">
          <CheckCircle2 className="w-3 h-3" /> Active
        </span>
      );
    }
    if (s === 'pending') {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 bg-amber-500/10 text-amber-400 border border-amber-500/20 rounded-lg text-[10px] font-black uppercase animate-pulse">
          <Clock className="w-3 h-3" /> Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-lg text-[10px] font-black uppercase">
        <XCircle className="w-3 h-3" /> Banned
      </span>
    );
  };

  const initialLetter = (name) => (name ? name.trim().charAt(0).toUpperCase() : '?');

  return (
    <div className="min-h-screen bg-zinc-950 font-sans text-zinc-100 w-full pt-4 lg:pt-20 lg:pl-72 transition-all duration-500">
      <style>{`
        @keyframes springIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-spring-in { animation: springIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        .shimmer-bg {
          background: linear-gradient(90deg, rgba(39,39,42,0.6) 25%, rgba(63,63,70,0.6) 50%, rgba(39,39,42,0.6) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.8s infinite;
        }
        .micro-interaction {
          transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .micro-interaction:hover { transform: translateY(-2px); }
        .micro-interaction:active { transform: translateY(0px) scale(0.98); }

        .custom-scrollbar::-webkit-scrollbar { width: 8px; height: 8px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: rgba(255,255,255,0.05); border-radius: 8px; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.2); border-radius: 8px; }
      `}</style>

      <div className="p-4 sm:p-8 max-w-[1600px] mx-auto space-y-8">

        {/* ==================== HEADER ==================== */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 animate-spring-in">
          <div>
            <h2 className="text-3xl font-black tracking-tight text-zinc-100">
              Publisher Network
            </h2>
            <p className="text-zinc-500 text-sm font-medium mt-1">
              Oversee accounts and monitor publisher health
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
            <div className="relative flex-1 sm:w-72 group">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 group-focus-within:text-emerald-400 transition-colors" />
              <input
                placeholder="Find a publisher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3.5 bg-zinc-900/90 border border-zinc-800 rounded-2xl text-sm font-bold shadow-lg outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 transition-all text-zinc-100 placeholder:text-zinc-600"
              />
            </div>

            <button
              onClick={openPendingModal}
              className="relative group px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 overflow-hidden shadow-xl shadow-emerald-500/20 smooth-transition micro-interaction"
            >
              <Clock className="w-4 h-4 relative z-10" />
              <span className="relative z-10">Pending Requests</span>
              {pending.length > 0 && (
                <span className="relative z-10 px-2 py-0.5 bg-zinc-950/20 text-zinc-950 rounded-md text-[9px] font-black">
                  {pending.length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* ==================== STATUS TABS ==================== */}
        <div className="flex bg-zinc-900/90 p-1 rounded-2xl w-full sm:w-fit border border-zinc-800 animate-spring-in">
          {[
            { key: 'all', label: 'All' },
            { key: 'active', label: 'Active' },
            { key: 'pending', label: 'Pending' },
            { key: 'banned', label: 'Banned' },
          ].map((tab) => {
            const count =
              tab.key === 'all'
                ? users.length
                : users.filter((u) => u.status === tab.key).length;
            return (
              <button
                key={tab.key}
                onClick={() => setStatusFilter(tab.key)}
                className={`flex-1 sm:flex-initial px-2 sm:px-5 py-2 sm:py-2.5 rounded-xl text-[8px] sm:text-[10px] font-black uppercase tracking-tight sm:tracking-widest transition-all flex items-center justify-center gap-1 ${
                  statusFilter === tab.key
                    ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                    : 'text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800/50'
                }`}
              >
                {tab.label}
                <span
                  className={`px-1 py-0.5 rounded-md text-[8px] sm:text-[9px] font-black ${
                    statusFilter === tab.key
                      ? 'bg-zinc-950/20 text-zinc-950'
                      : 'bg-zinc-800 text-zinc-400'
                  }`}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* ==================== TABLE ==================== */}
        <div className="bg-zinc-900/90 border border-zinc-800 rounded-[2.5rem] shadow-lg overflow-hidden animate-spring-in">
          <div className="overflow-x-auto overflow-y-auto max-h-[70vh] custom-scrollbar">
            <table className="w-full text-left border-collapse min-w-[1200px]">
              <thead className="sticky top-0 z-10 bg-zinc-900">
                <tr className="border-b border-zinc-800 text-[10px] font-black uppercase tracking-widest text-zinc-500">
                  <th className="px-8 py-6">Publisher Info</th>
                  <th className="px-8 py-6 text-center">Contact</th>
                  <th className="px-8 py-6 text-center">Wallet Details</th>
                  <th className="px-8 py-6 text-center">Network Stats</th>
                  <th className="px-8 py-6 text-center">Status</th>
                  <th className="px-8 py-6 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(6)].map((__, j) => (
                        <td key={j} className="px-8 py-6">
                          <div className="h-4 bg-zinc-800 rounded shimmer-bg" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-20 text-center">
                      <div className="flex flex-col items-center">
                        <Users className="w-10 h-10 text-zinc-700 mb-3" />
                        <p className="text-sm font-bold text-zinc-500 italic">
                          No users found matching your filters.
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr
                      key={user.id}
                      className="hover:bg-emerald-500/[0.03] transition-colors group"
                    >
                      {/* Publisher Info */}
                      <td className="px-8 py-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-zinc-950 rounded-2xl flex items-center justify-center text-lg font-black text-emerald-400 border border-zinc-800 group-hover:bg-emerald-500 group-hover:text-zinc-950 group-hover:border-emerald-500 transition-all duration-300 shrink-0">
                            {initialLetter(user.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="text-[15px] font-black text-zinc-100 leading-tight truncate max-w-[200px]">
                              {user.name || '—'}
                            </p>
                            <p className="text-[10px] font-bold text-zinc-500 uppercase mt-1 font-mono truncate max-w-[200px]">
                              ID: #{user.id}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Contact */}
                      <td className="px-8 py-6 text-center">
                        <p className="text-xs font-bold text-zinc-200 truncate max-w-[200px] mx-auto">
                          {user.email || '—'}
                        </p>
                        <p className="text-[10px] font-medium text-emerald-400 mt-1">
                          {user.number || '—'}
                        </p>
                      </td>

                      {/* Wallet */}
                      <td className="px-8 py-6 text-center">
                        <div className="inline-block text-left">
                          <p className="text-xs font-black text-emerald-400">
                            Balance: ₹{Number(user.balance || 0).toLocaleString()}
                          </p>
                          <p className="text-[10px] font-bold text-zinc-500 mt-0.5">
                            Paid: ₹{Number(user.totalWithdrawed || 0).toLocaleString()}
                          </p>
                        </div>
                      </td>

                      {/* Stats */}
                      <td className="px-8 py-6 text-center">
                        <div className="flex justify-center gap-4">
                          <div className="text-center">
                            <p className="text-xs font-black text-zinc-100">
                              {user.totalClicks || 0}
                            </p>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase">
                              Clicks
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-black text-zinc-100">
                              {user.totalConversions || 0}
                            </p>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase">
                              Convs
                            </p>
                          </div>
                          <div className="text-center">
                            <p className="text-xs font-black text-emerald-400">
                              ₹{Number(user.totalEarned || 0).toLocaleString()}
                            </p>
                            <p className="text-[9px] font-bold text-zinc-500 uppercase">
                              Earned
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Status */}
                      <td className="px-8 py-6 text-center">
                        {statusBadge(user.status)}
                      </td>

                      {/* Actions */}
                      <td className="px-8 py-6 text-right">
                        <div className="flex justify-end gap-2.5">
                          {user.status === 'banned' ? (
                            <button
                              onClick={() => openConfirm('unban', user)}
                              title="Unban User"
                              className="p-3 rounded-2xl transition-all shadow-sm bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500 hover:text-zinc-950 smooth-transition micro-interaction"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => openConfirm('ban', user)}
                              title="Ban User"
                              className="p-3 rounded-2xl transition-all shadow-sm bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500 hover:text-zinc-950 smooth-transition micro-interaction"
                            >
                              <ShieldAlert className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openConfirm('delete', user)}
                            title="Delete User"
                            className="p-3 rounded-2xl transition-all shadow-sm bg-rose-500/10 text-rose-400 border border-rose-500/20 hover:bg-rose-500 hover:text-white smooth-transition micro-interaction"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* ==================== PAGINATION FOOTER ==================== */}
          {!loading && filteredUsers.length > 0 && (
            <div className="p-6 bg-zinc-950/40 border-t border-zinc-800 flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-[10px] font-black text-zinc-500 uppercase tracking-widest">
                Showing {(currentPage - 1) * ROWS_PER_PAGE + 1} to{' '}
                {Math.min(currentPage * ROWS_PER_PAGE, filteredUsers.length)} of{' '}
                {filteredUsers.length} results
              </p>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <div className="flex gap-1">
                  {[...Array(totalPages)].map((_, i) => {
                    const p = i + 1;
                    if (
                      totalPages > 7 &&
                      p !== 1 &&
                      p !== totalPages &&
                      Math.abs(p - currentPage) > 1
                    ) {
                      if (p === 2 || p === totalPages - 1) {
                        return (
                          <span key={p} className="px-2 py-2 text-xs font-black text-zinc-600">
                            …
                          </span>
                        );
                      }
                      return null;
                    }
                    return (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        className={`px-3.5 py-2 rounded-xl text-xs font-black transition-all ${
                          currentPage === p
                            ? 'bg-emerald-500 text-zinc-950 shadow-lg shadow-emerald-500/20'
                            : 'text-zinc-400 hover:bg-zinc-800/60 hover:text-zinc-100'
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 bg-zinc-900 border border-zinc-800 rounded-xl text-zinc-400 hover:text-emerald-400 hover:border-emerald-500/50 disabled:opacity-30 disabled:cursor-not-allowed transition-all micro-interaction"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

      </div>

      {/* ==================== CONFIRM ACTION MODAL ==================== */}
      {confirmAction && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4">
          <div
            onClick={closeConfirm}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
          />
          <div className="relative w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-[2.5rem] shadow-2xl p-8 text-center space-y-5 animate-spring-in">
            <div
              className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto border ${
                confirmAction.type === 'ban'
                  ? 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                  : confirmAction.type === 'delete'
                  ? 'bg-rose-500/10 text-rose-400 border-rose-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20'
              }`}
            >
              {confirmAction.type === 'ban' ? (
                <ShieldAlert className="w-8 h-8" />
              ) : confirmAction.type === 'delete' ? (
                <AlertTriangle className="w-8 h-8" />
              ) : (
                <CheckCircle2 className="w-8 h-8" />
              )}
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black text-zinc-100 tracking-tight capitalize">
                {confirmAction.type} User?
              </h3>
              <p className="text-xs text-zinc-400 font-medium leading-relaxed">
                Are you sure you want to {confirmAction.type}{' '}
                <span className="text-zinc-100 font-bold">
                  {confirmAction.user.name || 'this user'}
                </span>
                ?{' '}
                {confirmAction.type === 'delete'
                  ? 'This action cannot be undone.'
                  : 'You can reverse this action later.'}
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={closeConfirm}
                className="flex-1 py-3 bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl font-black text-xs uppercase tracking-wider hover:border-zinc-700 smooth-transition micro-interaction"
              >
                Cancel
              </button>
              <button
                onClick={() => performAction(confirmAction.user.id, confirmAction.type)}
                disabled={actionLoading}
                className={`flex-1 py-3 rounded-xl font-black text-xs uppercase tracking-wider shadow-lg smooth-transition micro-interaction disabled:opacity-50 flex items-center justify-center gap-2 ${
                  confirmAction.type === 'ban'
                    ? 'bg-amber-500 hover:bg-amber-400 text-zinc-950 shadow-amber-500/20'
                    : confirmAction.type === 'delete'
                    ? 'bg-rose-500 hover:bg-rose-400 text-white shadow-rose-500/20'
                    : 'bg-emerald-500 hover:bg-emerald-400 text-zinc-950 shadow-emerald-500/20'
                }`}
              >
                {actionLoading ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : null}
                {actionLoading ? 'Processing...' : `Confirm ${confirmAction.type}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== PENDING REQUESTS MODAL ==================== */}
      {isPendingModalOpen && (
        <div className="fixed inset-0 z-[250] flex items-center justify-center p-4 sm:p-6">
          <div
            onClick={closePendingModal}
            className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
              modalAnimating ? 'opacity-100' : 'opacity-0'
            }`}
          />
          <div
            className={`relative w-full max-w-5xl bg-zinc-900 rounded-[2.5rem] border border-zinc-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh] transition-all duration-300 ${
              modalAnimating ? 'animate-spring-in opacity-100' : 'opacity-0 scale-95'
            }`}
          >
            {/* Header */}
            <div className="p-6 sm:p-8 border-b border-zinc-800 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-zinc-950/50 shrink-0">
              <div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-ping" />
                  <h2 className="text-2xl font-black text-zinc-100 tracking-tight">
                    Pending Approvals
                  </h2>
                </div>
                <p className="text-[10px] text-zinc-500 font-bold uppercase tracking-widest mt-1">
                  Review new publisher applications
                </p>
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto">
                <div className="relative flex-1 sm:w-64">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                  <input
                    placeholder="Search applications..."
                    value={pendingSearch}
                    onChange={(e) => setPendingSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-zinc-950 border border-zinc-800 rounded-xl text-xs font-bold outline-none focus:border-emerald-500/60 focus:ring-2 focus:ring-emerald-500/15 text-zinc-100 placeholder:text-zinc-600"
                  />
                </div>
                <button
                  onClick={closePendingModal}
                  className="p-2.5 bg-zinc-950 border border-zinc-800 text-zinc-400 hover:text-rose-400 rounded-xl transition-all micro-interaction"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-auto custom-scrollbar">
              {filteredPending.length === 0 ? (
                <div className="py-20 text-center flex flex-col items-center">
                  <UserPlus className="w-12 h-12 text-zinc-700 mb-4" />
                  <p className="text-sm font-bold text-zinc-500">
                    No pending applications at the moment.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse min-w-[900px]">
                  <thead className="sticky top-0 z-10 bg-zinc-900">
                    <tr className="bg-zinc-950/50 text-[10px] font-black uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                      <th className="px-6 py-4">User Details</th>
                      <th className="px-6 py-4">Contact Info</th>
                      <th className="px-6 py-4">Applied On</th>
                      <th className="px-6 py-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800/60">
                    {filteredPending.map((user) => (
                      <tr
                        key={user.id}
                        className="hover:bg-emerald-500/[0.03] transition-colors group"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-zinc-950 rounded-xl flex items-center justify-center text-sm font-black text-amber-400 border border-zinc-800">
                              {initialLetter(user.name)}
                            </div>
                            <div>
                              <p className="text-sm font-black text-zinc-100">
                                {user.name || '—'}
                              </p>
                              <p className="text-[10px] font-mono text-zinc-500">
                                #{user.id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-xs font-bold text-zinc-200">
                            {user.email || '—'}
                          </p>
                          <p className="text-[10px] text-emerald-400 mt-0.5">
                            {user.number || '—'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-[11px] text-zinc-400 font-mono">
                            {user.createdAt || '—'}
                          </p>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => performAction(user.id, 'reject')}
                              className="px-3 py-2 bg-rose-500/10 text-rose-400 border border-rose-500/20 rounded-xl text-[10px] font-black uppercase tracking-wider hover:bg-rose-500 hover:text-white transition-all micro-interaction"
                            >
                              Reject
                            </button>
                            <button
                              onClick={() => performAction(user.id, 'approve')}
                              className="px-3 py-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all micro-interaction shadow-lg shadow-emerald-500/20"
                            >
                              Approve
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminUsers;